import {
	useApiGetQuestionMetrics,
	useApiGetQuestionTimeseries,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { keepPreviousData } from '@tanstack/react-query';
import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../api';
import { AnalyticsBanner } from './components/AnalyticsBanner';
import { MetricsQuestionCards } from './components/cards/MetricsQuestionCards';
import { QuestionsOverviewChart } from './components/charts/QuestionsOverviewChart';
import { MetricsOverviewTab } from './components/MetricsOverviewTab';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { questionsFilterOptions } from './constants';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';
import type { MetricCardType, QuestionFilterType } from './types';

import { isValidTimeRange, isValidViewOption } from './types.ts';
interface QuestionsOverviewTabControllerProps {
	teamId: string;
	onMetricCardClick?: (metricType: MetricCardType) => void;
}

export const QuestionsOverviewTabController = memo(
	({ teamId, onMetricCardClick }: QuestionsOverviewTabControllerProps) => {
		const { user, workspace } = useAuthUser();
		const {
			timeRangeValue,
			setTimeRangeValue,
			dateRangeOption,
			setDateRangeOption,
			dateFilter,
			setDateFilter,
		} = useOverviewMetricDatetime({ cacheKey: 'questions' });
		const navigate = useNavigate();
		const [filterType, setFilterType] = useState<QuestionFilterType>('all');

		// Create wrapper functions to handle type conversion with validation
		const handleSetDateFilter = useCallback(
			(value: string) => {
				if (isValidTimeRange(value)) {
					setDateFilter(value);
				}
			},
			[setDateFilter]
		);

		const handleSetDateRangeOption = useCallback(
			(value: string) => {
				if (isValidViewOption(value)) {
					setDateRangeOption(value);
				}
			},
			[setDateRangeOption]
		);

		const { data: metrics, isPending: isMetricsLoading } =
			useApiGetQuestionMetrics(
				{
					queryParams: {
						team_id: teamId,
						filter_type: filterType,
						current_state_only: true,
						...(filterType !== 'all' && { user_id: user?.id }),
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const { data: timeseries, isPending: isTimeseriesLoading } =
			useApiGetQuestionTimeseries(
				{
					queryParams: {
						team_id: teamId,
						filter_type: filterType,
						time_range: dateFilter,
						view_option: dateRangeOption,
						...(filterType !== 'all' && { user_id: user?.id }),
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const handleFilterTypesChange = useCallback(
			(filterTypes: string[]) => {
				const newFilterType = filterTypes[0] as QuestionFilterType;
				setFilterType(newFilterType);

				const filterLabel = questionsFilterOptions.find(
					(opt) => opt.value === newFilterType
				)?.label;
				if (!filterLabel) return;

				trackEvent(
					'questions/overview/filter_change',
					{
						filter_type: filterLabel,
						filter_value: newFilterType,
					},
					user,
					workspace!
				);
			},
			[user, workspace]
		);

		const handleNavigate = useCallback(() => {
			navigate(`/analytics`);
		}, [navigate]);

		if (isMetricsLoading || isTimeseriesLoading || !timeseries || !metrics) {
			return <MetricsOverviewTabSkeleton cards={5} />;
		}

		return (
			<MetricsOverviewTab
				filterOptions={[questionsFilterOptions]}
				filterTypes={[filterType]}
				setFilterTypes={handleFilterTypesChange}
			>
				<MetricsQuestionCards
					questionMetrics={metrics}
					onMetricCardClick={onMetricCardClick}
				/>
				<AnalyticsBanner handleNavigate={handleNavigate} />
				<QuestionsOverviewChart
					timeseries={timeseries}
					dateFilter={dateFilter || '24h'}
					setDateFilter={handleSetDateFilter}
					setTimeRangeValue={setTimeRangeValue}
					timeRangeValue={timeRangeValue}
					dateRangeOption={dateRangeOption || 'day'}
					setDateRangeOption={handleSetDateRangeOption}
				/>
			</MetricsOverviewTab>
		);
	}
);
